<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ProgressBar from 'dashboard/components-next/progress/ProgressBar.vue';
import Modal from 'dashboard/components/Modal.vue';

import { REPORTING_DEFAULT_TIMEZONE } from 'dashboard/helper/reportingTimezone';
const store = useStore();
const router = useRouter();
const { t, locale } = useI18n();
const { showAlert } = useAlert();
const { checkPermissions } = usePolicy();
const { accountScopedRoute } = useAccount();

const isAdmin = computed(() => checkPermissions(['administrator']));

const isLoading = ref(true);
const loadError = ref(null);
const searchQuery = ref('');
const selectedStatus = ref('');
const pendingAction = ref(null);
const actionOperationToken = ref(null);
const currentAccountId = computed(() => store.getters.getCurrentAccountId);

const currentAccount = computed(
  () =>
    store.getters['accounts/getAccount']?.(currentAccountId.value) ||
    store.getters.getCurrentAccount
);
const reportingTimezone = computed(
  () =>
    currentAccount.value?.reporting_timezone ||
    currentAccount.value?.settings?.reporting_timezone ||
    REPORTING_DEFAULT_TIMEZONE
);
const localeForIntl = computed(() =>
  String(locale.value || 'en-US').replace('_', '-')
);

// B2: ref para delete (já existia, mantido)
const broadcastToDelete = ref(null);
const deleteModalOpen = computed({
  get: () => Boolean(broadcastToDelete.value),
  set: show => {
    if (!show && pendingAction.value !== 'delete') {
      broadcastToDelete.value = null;
    }
  },
});

// B1: ref para cancel (novo)
const broadcastToCancel = ref(null);
const cancelModalOpen = computed({
  get: () => Boolean(broadcastToCancel.value),
  set: show => {
    if (!show && pendingAction.value !== 'cancel') {
      broadcastToCancel.value = null;
    }
  },
});

const STATUS_OPTIONS = computed(() => [
  { value: '', label: t('BROADCASTS.STATUS_FILTER_ALL') },
  { value: 'pending', label: t('BROADCASTS.STATUS.PENDING') },
  { value: 'running', label: t('BROADCASTS.STATUS.RUNNING') },
  { value: 'paused', label: t('BROADCASTS.STATUS.PAUSED') },
  { value: 'completed', label: t('BROADCASTS.STATUS.COMPLETED') },
  { value: 'cancelled', label: t('BROADCASTS.STATUS.CANCELLED') },
]);

const STATUS_ICON = {
  pending: {
    icon: 'i-lucide-clock',
    wrapper: 'bg-n-amber-3 text-n-amber-11 ring-n-amber-4',
  },
  running: {
    icon: 'i-lucide-radio-tower',
    wrapper: 'bg-n-teal-3 text-n-teal-11 ring-n-teal-4',
  },
  paused: {
    icon: 'i-lucide-pause',
    wrapper: 'bg-n-amber-3 text-n-amber-11 ring-n-amber-4',
  },
  completed: {
    icon: 'i-lucide-check-circle-2',
    wrapper: 'bg-n-iris-3 text-n-iris-11 ring-n-iris-4',
  },
  cancelled: {
    icon: 'i-lucide-ban',
    wrapper: 'bg-n-alpha-2 text-n-slate-10 ring-n-strong',
  },
  failed: {
    icon: 'i-lucide-alert-triangle',
    wrapper: 'bg-n-ruby-3 text-n-ruby-11 ring-n-ruby-4',
  },
};

const STATUS_BADGE = {
  pending: 'bg-n-amber-3 text-n-amber-11 border border-n-amber-4',
  running: 'bg-n-teal-3 text-n-teal-11 border border-n-teal-4',
  paused: 'bg-n-amber-3 text-n-amber-11 border border-n-amber-4',
  completed: 'bg-n-iris-3 text-n-iris-11 border border-n-iris-4',
  cancelled: 'bg-n-alpha-2 text-n-slate-10 border border-n-strong',
  failed: 'bg-n-ruby-3 text-n-ruby-11 border border-n-ruby-4',
};

const allBroadcasts = computed(
  () => store.getters['broadcasts/getAllBroadcasts'] || []
);

const filteredBroadcasts = computed(() => {
  let list = allBroadcasts.value;
  if (selectedStatus.value) {
    list = list.filter(b => b.status === selectedStatus.value);
  }
  if (searchQuery.value.trim()) {
    const q = searchQuery.value.trim().toLowerCase();
    list = list.filter(b => b.name && b.name.toLowerCase().includes(q));
  }
  return list;
});

// B3: totalCount vem do store (todos), filteredCount reflete filtro ativo
const totalCount = computed(() => allBroadcasts.value.length);
const filteredCount = computed(() => filteredBroadcasts.value.length);

// B4: detecta filtro ativo
const hasActiveFilter = computed(
  () => searchQuery.value.trim() !== '' || selectedStatus.value !== ''
);

// B3: counter label — mostra "X de Y" quando filtrado
const countLabel = computed(() => {
  if (hasActiveFilter.value && filteredCount.value !== totalCount.value) {
    return t('BROADCASTS.COUNT_FILTERED', {
      filtered: filteredCount.value,
      total: totalCount.value,
    });
  }
  if (totalCount.value === 0) return t('BROADCASTS.COUNT_ZERO');
  if (totalCount.value === 1) return t('BROADCASTS.COUNT_SINGULAR');
  return t('BROADCASTS.COUNT_PLURAL', { count: totalCount.value });
});

const hasRunningBroadcasts = computed(() =>
  allBroadcasts.value.some(b => b.status === 'running')
);

const progressPercent = broadcast => {
  if (!broadcast.total_count || broadcast.total_count === 0) return 0;
  return Math.round(
    ((broadcast.sent_count + broadcast.failed_count) / broadcast.total_count) *
      100
  );
};

const statusLabel = status => {
  const map = {
    pending: t('BROADCASTS.STATUS.PENDING'),
    running: t('BROADCASTS.STATUS.RUNNING'),
    paused: t('BROADCASTS.STATUS.PAUSED'),
    completed: t('BROADCASTS.STATUS.COMPLETED'),
    cancelled: t('BROADCASTS.STATUS.CANCELLED'),
    failed: t('BROADCASTS.STATUS.FAILED'),
  };
  return map[status] || status;
};

const statusBadgeClass = status =>
  STATUS_BADGE[status] || 'bg-n-alpha-1 text-n-slate-11 border border-n-weak';

const statusIconConfig = status =>
  STATUS_ICON[status] || {
    icon: 'i-lucide-megaphone',
    wrapper: 'bg-n-alpha-2 text-n-slate-10 ring-n-strong',
  };

const formatDate = dateStr => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(localeForIntl.value, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: reportingTimezone.value,
  }).format(d);
};

let accountGeneration = 0;
const isCurrentAccountContext = (accountId, generation) =>
  String(accountId) === String(currentAccountId.value) &&
  generation === accountGeneration;

const captureOperationContext = tokenRef => {
  const context = {
    accountId: currentAccountId.value,
    generation: accountGeneration,
    token: Symbol('broadcast-operation'),
  };
  tokenRef.value = context.token;
  return context;
};

const isCurrentOperationContext = (context, tokenRef) =>
  tokenRef.value === context.token &&
  isCurrentAccountContext(context.accountId, context.generation);

const mutationPayload = (id, context) => ({
  id,
  accountId: context.accountId,
  generation: context.generation,
});

const loadBroadcasts = async ({ accountId, generation } = {}) => {
  const requestAccountId = accountId ?? currentAccountId.value;
  const requestGeneration = generation ?? accountGeneration;
  isLoading.value = true;
  loadError.value = null;
  try {
    await store.dispatch('broadcasts/list', {
      accountId: requestAccountId,
      generation: requestGeneration,
    });
  } catch {
    if (isCurrentAccountContext(requestAccountId, requestGeneration)) {
      loadError.value = t('BROADCASTS.TOAST.ERROR_GENERIC');
    }
  } finally {
    if (isCurrentAccountContext(requestAccountId, requestGeneration)) {
      isLoading.value = false;
    }
  }
};

const handlePause = async broadcast => {
  const actionKey = `pause:${broadcast.id}`;
  if (pendingAction.value) return;
  const broadcastId = broadcast.id;
  const operationContext = captureOperationContext(actionOperationToken);
  pendingAction.value = actionKey;
  try {
    await store.dispatch(
      'broadcasts/pause',
      mutationPayload(broadcastId, operationContext)
    );
    if (!isCurrentOperationContext(operationContext, actionOperationToken)) {
      return;
    }
    showAlert(t('BROADCASTS.TOAST.PAUSED'));
  } catch {
    if (!isCurrentOperationContext(operationContext, actionOperationToken)) {
      return;
    }
    showAlert(t('BROADCASTS.TOAST.ERROR_GENERIC'));
  } finally {
    if (isCurrentOperationContext(operationContext, actionOperationToken)) {
      pendingAction.value = null;
      actionOperationToken.value = null;
    }
  }
};

const handleResume = async broadcast => {
  const actionKey = `resume:${broadcast.id}`;
  if (pendingAction.value) return;
  const broadcastId = broadcast.id;
  const operationContext = captureOperationContext(actionOperationToken);
  pendingAction.value = actionKey;
  try {
    await store.dispatch(
      'broadcasts/resume',
      mutationPayload(broadcastId, operationContext)
    );
    if (!isCurrentOperationContext(operationContext, actionOperationToken)) {
      return;
    }
    showAlert(t('BROADCASTS.TOAST.RESUMED'));
  } catch {
    if (!isCurrentOperationContext(operationContext, actionOperationToken)) {
      return;
    }
    showAlert(t('BROADCASTS.TOAST.ERROR_GENERIC'));
  } finally {
    if (isCurrentOperationContext(operationContext, actionOperationToken)) {
      pendingAction.value = null;
      actionOperationToken.value = null;
    }
  }
};

// B2: delete via modal (já existia, mantido com mesma lógica)
const handleDelete = broadcast => {
  broadcastToDelete.value = broadcast;
};

const confirmDelete = async () => {
  if (!broadcastToDelete.value || pendingAction.value) return;
  const broadcastId = broadcastToDelete.value.id;
  const operationContext = captureOperationContext(actionOperationToken);
  pendingAction.value = 'delete';
  try {
    await store.dispatch(
      'broadcasts/delete',
      mutationPayload(broadcastId, operationContext)
    );
    if (!isCurrentOperationContext(operationContext, actionOperationToken)) {
      return;
    }
    showAlert(t('BROADCASTS.TOAST.DELETED'));
    broadcastToDelete.value = null;
  } catch {
    if (!isCurrentOperationContext(operationContext, actionOperationToken)) {
      return;
    }
    showAlert(t('BROADCASTS.TOAST.ERROR_GENERIC'));
  } finally {
    if (isCurrentOperationContext(operationContext, actionOperationToken)) {
      pendingAction.value = null;
      actionOperationToken.value = null;
    }
  }
};

const closeDelete = () => {
  if (pendingAction.value === 'delete') return;
  broadcastToDelete.value = null;
};

// B1: cancel via modal (novo)
const requestCancel = broadcast => {
  broadcastToCancel.value = broadcast;
};

const confirmCancel = async () => {
  if (!broadcastToCancel.value || pendingAction.value) return;
  const broadcastId = broadcastToCancel.value.id;
  const operationContext = captureOperationContext(actionOperationToken);
  pendingAction.value = 'cancel';
  try {
    await store.dispatch(
      'broadcasts/cancel',
      mutationPayload(broadcastId, operationContext)
    );
    if (!isCurrentOperationContext(operationContext, actionOperationToken)) {
      return;
    }
    showAlert(t('BROADCASTS.TOAST.CANCELLED'));
    broadcastToCancel.value = null;
  } catch {
    if (!isCurrentOperationContext(operationContext, actionOperationToken)) {
      return;
    }
    showAlert(t('BROADCASTS.TOAST.ERROR_GENERIC'));
  } finally {
    if (isCurrentOperationContext(operationContext, actionOperationToken)) {
      pendingAction.value = null;
      actionOperationToken.value = null;
    }
  }
};

const closeCancel = () => {
  if (pendingAction.value === 'cancel') return;
  broadcastToCancel.value = null;
};

const handleViewDetail = broadcast => {
  router.push(
    accountScopedRoute('broadcasts_detail', { broadcastId: broadcast.id })
  );
};

const isDuplicating = ref(null);
const duplicateOperationToken = ref(null);

const handleDuplicate = async broadcast => {
  if (isDuplicating.value !== null) return;
  const broadcastId = broadcast.id;
  const operationContext = captureOperationContext(duplicateOperationToken);
  isDuplicating.value = broadcastId;
  try {
    await store.dispatch(
      'broadcasts/duplicate',
      mutationPayload(broadcastId, operationContext)
    );
    if (!isCurrentOperationContext(operationContext, duplicateOperationToken)) {
      return;
    }
    showAlert(t('BROADCASTS.TOAST.DUPLICATED'));
  } catch {
    if (!isCurrentOperationContext(operationContext, duplicateOperationToken)) {
      return;
    }
    showAlert(t('BROADCASTS.TOAST.ERROR_GENERIC'));
  } finally {
    if (isCurrentOperationContext(operationContext, duplicateOperationToken)) {
      isDuplicating.value = null;
      duplicateOperationToken.value = null;
    }
  }
};

const handleEdit = broadcast => {
  router.push(
    accountScopedRoute('broadcasts_edit', { broadcastId: broadcast.id })
  );
};

// B4: limpar filtros
const clearFilters = () => {
  searchQuery.value = '';
  selectedStatus.value = '';
};

// Auto-refresh every 10s if there are running broadcasts
let refreshInterval = null;

const stopAutoRefresh = () => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
};

const startAutoRefresh = () => {
  stopAutoRefresh();
  refreshInterval = setInterval(async () => {
    if (hasRunningBroadcasts.value) {
      await store.dispatch('broadcasts/list', {
        accountId: currentAccountId.value,
        generation: accountGeneration,
      });
    }
  }, 10000);
};

onMounted(async () => {
  accountGeneration += 1;
  const generation = accountGeneration;
  const accountId = currentAccountId.value;
  await loadBroadcasts({ accountId, generation });
  if (isCurrentAccountContext(accountId, generation)) startAutoRefresh();
});

watch(
  currentAccountId,
  async (accountId, previousAccountId) => {
    if (String(accountId) === String(previousAccountId)) return;

    accountGeneration += 1;
    const generation = accountGeneration;
    stopAutoRefresh();
    store.commit('broadcasts/RESET_ACCOUNT_DATA');
    broadcastToDelete.value = null;
    broadcastToCancel.value = null;
    pendingAction.value = null;
    actionOperationToken.value = null;
    isDuplicating.value = null;
    duplicateOperationToken.value = null;
    searchQuery.value = '';
    selectedStatus.value = '';
    loadError.value = null;
    isLoading.value = true;

    await loadBroadcasts({ accountId, generation });
    if (isCurrentAccountContext(accountId, generation)) startAutoRefresh();
  },
  { flush: 'sync' }
);

onBeforeUnmount(() => {
  accountGeneration += 1;
  actionOperationToken.value = null;
  duplicateOperationToken.value = null;
  stopAutoRefresh();
});
</script>

<template>
  <div class="flex flex-col flex-1 overflow-hidden bg-n-background">
    <!-- Header Global Premium -->
    <div
      class="px-6 lg:px-8 py-6 relative overflow-hidden bg-gradient-to-b from-n-alpha-1/50 to-transparent border-b border-n-weak/80"
    >
      <div
        class="absolute inset-0 bg-gradient-to-r from-transparent via-n-brand/5 to-transparent pointer-events-none opacity-50"
      />

      <div
        class="flex items-center justify-between gap-4 min-w-0 relative z-10"
      >
        <div class="flex items-center gap-4">
          <div
            class="size-12 rounded-[1.25rem] flex items-center justify-center flex-shrink-0 ring-1 ring-black/5 shadow-sm bg-n-brand/10 text-n-brand"
          >
            <span
              class="i-lucide-megaphone size-6 drop-shadow-sm"
              aria-hidden="true"
            />
          </div>
          <div class="min-w-0">
            <h1
              class="text-[1.35rem] font-bold text-n-slate-12 tracking-tight truncate leading-tight"
            >
              {{ t('BROADCASTS.PAGE_TITLE') }}
            </h1>
            <p
              class="text-sm font-medium text-n-slate-10 mt-0.5 truncate tracking-wide"
            >
              {{ countLabel }}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <button
            type="button"
            class="hidden min-h-11 sm:flex items-center gap-1.5 rounded-xl border border-n-strong px-4 py-2.5 text-sm font-semibold text-n-slate-12 bg-n-background shadow-sm hover:bg-n-alpha-1/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand/20"
            @click="$router.push(accountScopedRoute('broadcasts_engagement'))"
          >
            <span class="i-lucide-bar-chart-3 size-4" aria-hidden="true" />
            {{ t('BROADCASTS.ENGAGEMENT_BUTTON') }}
          </button>
          <button
            type="button"
            class="hidden min-h-11 sm:flex items-center gap-1.5 rounded-xl border border-n-strong px-4 py-2.5 text-sm font-semibold text-n-slate-12 bg-n-background shadow-sm hover:bg-n-alpha-1/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand/20"
            @click="$router.push(accountScopedRoute('broadcasts_blacklist'))"
          >
            <span class="i-lucide-shield-ban size-4" aria-hidden="true" />
            {{ t('BROADCASTS.BLACKLIST_BUTTON') }}
          </button>
          <button
            type="button"
            class="flex min-h-11 items-center gap-1.5 rounded-xl bg-n-brand px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:shadow-lg hover:shadow-n-brand/20 transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-n-brand"
            @click="$router.push(accountScopedRoute('broadcasts_new'))"
          >
            <span class="i-lucide-plus size-4.5" aria-hidden="true" />
            {{ t('BROADCASTS.NEW_BUTTON') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Toolbar: search + filter Premium -->
    <div
      class="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 px-6 lg:px-8 py-5 border-b border-n-weak/50 bg-n-alpha-1/10"
    >
      <div class="relative flex-1 max-w-md group">
        <span
          class="i-lucide-search absolute left-4 top-1/2 -translate-y-1/2 size-4.5 text-n-slate-10 group-focus-within:text-n-brand transition-colors pointer-events-none"
          aria-hidden="true"
        />
        <Input
          v-model="searchQuery"
          type="search"
          :placeholder="t('BROADCASTS.SEARCH_PLACEHOLDER')"
          :aria-label="t('BROADCASTS.SEARCH_PLACEHOLDER')"
          custom-input-class="!h-11 !pl-11 !pr-4 !rounded-xl !bg-n-background font-medium shadow-sm"
        />
      </div>
      <Select
        v-model="selectedStatus"
        class="min-w-[200px]"
        :options="STATUS_OPTIONS"
        :aria-label="t('BROADCASTS.STATUS_FILTER_ALL')"
      />
    </div>

    <!-- Error banner -->
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
    >
      <div
        v-if="loadError && !isLoading"
        class="mx-6 lg:mx-8 mt-5 flex items-center justify-between rounded-xl border border-n-ruby-5 bg-gradient-to-r from-n-ruby-2 to-n-ruby-1 text-n-ruby-11 px-5 py-4 text-sm shadow-sm"
        role="alert"
      >
        <div class="flex items-center gap-3">
          <span class="i-lucide-alert-triangle size-5" aria-hidden="true" />
          <span class="font-medium">{{ loadError }}</span>
        </div>
        <button
          type="button"
          class="ml-auto min-h-11 bg-n-ruby-3 hover:bg-n-ruby-4 text-n-ruby-12 px-4 py-2 rounded-lg font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-ruby-5"
          @click="
            loadError = null;
            loadBroadcasts();
          "
        >
          {{ t('BROADCASTS.RETRY') }}
        </button>
      </div>
    </Transition>

    <div
      class="flex-1 overflow-y-auto px-6 lg:px-8 py-6 relative custom-scrollbar"
    >
      <!-- Loading Skeleton Premium -->
      <div
        v-if="isLoading"
        role="status"
        :aria-label="t('BROADCASTS.LOADING')"
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
      >
        <div
          v-for="i in 6"
          :key="`skeleton-${i}`"
          class="h-44 rounded-[1.25rem] bg-gradient-to-r from-n-alpha-1 to-n-alpha-2 animate-pulse motion-reduce:animate-none ring-1 ring-black/5"
        >
          <div class="p-5 flex flex-col gap-4 h-full">
            <div class="flex items-center gap-3">
              <div class="size-12 rounded-[1rem] bg-n-background/50" />
              <div class="space-y-2 flex-1">
                <div class="h-4 bg-n-background/50 rounded w-3/4" />
                <div class="h-3 bg-n-background/50 rounded w-1/2" />
              </div>
            </div>
            <div class="mt-auto space-y-2">
              <div class="h-2 bg-n-background/50 rounded-full w-full" />
              <div class="h-3 bg-n-background/50 rounded w-1/4" />
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state — sem resultados de filtro -->
      <Transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 translate-y-4"
      >
        <div
          v-if="
            !isLoading && filteredBroadcasts.length === 0 && hasActiveFilter
          "
          class="flex flex-col items-center justify-center py-20 text-center rounded-[1.25rem] border border-dashed border-n-strong/50 bg-n-alpha-1/30"
        >
          <div
            class="size-20 rounded-3xl bg-n-alpha-2 flex items-center justify-center mb-5 ring-1 ring-white/10 shadow-inner"
          >
            <span
              class="i-lucide-search-x size-10 text-n-slate-9"
              aria-hidden="true"
            />
          </div>
          <h3 class="text-lg font-bold text-n-slate-12 mb-2 tracking-tight">
            {{ t('BROADCASTS.NO_RESULTS_TITLE') }}
          </h3>
          <p
            class="text-sm font-medium text-n-slate-10 max-w-sm leading-relaxed mb-5"
          >
            {{ t('BROADCASTS.NO_RESULTS_HINT') }}
          </p>
          <button
            type="button"
            class="inline-flex min-h-11 items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl border border-n-strong bg-n-background text-n-slate-12 transition-colors hover:bg-n-alpha-1/50 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            @click="clearFilters"
          >
            {{ t('BROADCASTS.CLEAR_FILTERS') }}
          </button>
        </div>
      </Transition>

      <!-- Empty state — zero state (sem nenhum disparo) -->
      <Transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 translate-y-4"
      >
        <div
          v-if="
            !isLoading && filteredBroadcasts.length === 0 && !hasActiveFilter
          "
          class="flex flex-col items-center justify-center py-20 text-center rounded-[1.25rem] border border-dashed border-n-strong/50 bg-n-alpha-1/30"
        >
          <div
            class="size-20 rounded-3xl bg-n-brand/10 text-n-brand flex items-center justify-center mb-5 ring-1 ring-n-brand/20 shadow-inner"
          >
            <span class="i-lucide-megaphone size-10" aria-hidden="true" />
          </div>
          <h3 class="text-lg font-bold text-n-slate-12 mb-2 tracking-tight">
            {{ t('BROADCASTS.EMPTY_STATE_TITLE') }}
          </h3>
          <p
            class="text-sm font-medium text-n-slate-10 max-w-sm leading-relaxed mb-5"
          >
            {{ t('BROADCASTS.EMPTY_STATE_HINT') }}
          </p>
          <button
            type="button"
            class="inline-flex min-h-11 items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-n-brand hover:bg-n-brand/90 rounded-xl shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            @click="$router.push(accountScopedRoute('broadcasts_new'))"
          >
            <span class="i-lucide-plus size-4.5" aria-hidden="true" />
            {{ t('BROADCASTS.NEW_BUTTON') }}
          </button>
        </div>
      </Transition>

      <!-- Cards Grid Premium -->
      <div
        v-if="!isLoading && filteredBroadcasts.length > 0"
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
      >
        <article
          v-for="broadcast in filteredBroadcasts"
          :key="broadcast.id"
          class="group relative flex flex-col p-5 rounded-[1.25rem] border border-n-weak bg-n-background hover:bg-n-alpha-1/20 transition-[background-color,border-color,box-shadow] duration-200 shadow-sm hover:shadow-md hover:border-n-brand/30 overflow-hidden"
        >
          <div
            class="absolute inset-0 bg-gradient-to-br from-n-brand/0 to-n-brand/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
          />

          <div
            class="flex items-start justify-between gap-3 mb-4 relative z-10"
          >
            <div class="flex items-start gap-3 min-w-0">
              <div
                :class="statusIconConfig(broadcast.status).wrapper"
                class="flex size-11 flex-shrink-0 items-center justify-center rounded-[1rem] shadow-sm ring-1 ring-inset"
              >
                <span
                  :class="statusIconConfig(broadcast.status).icon"
                  class="size-5"
                  aria-hidden="true"
                />
              </div>
              <div class="min-w-0 pt-0.5">
                <h3 class="truncate text-[15px] font-bold">
                  <button
                    type="button"
                    class="min-h-11 max-w-full truncate rounded text-left text-n-slate-12 group-hover:text-n-brand focus-visible:outline-2 focus-visible:outline-n-brand transition-colors"
                    @click="handleViewDetail(broadcast)"
                  >
                    {{ broadcast.name }}
                  </button>
                </h3>
                <div class="mt-1 flex items-center gap-2">
                  <span
                    :class="statusBadgeClass(broadcast.status)"
                    class="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide shadow-sm"
                  >
                    <span class="size-1.5 rounded-full bg-current" />
                    {{ statusLabel(broadcast.status) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Quick Actions Top Right -->
            <div
              class="flex items-center opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
              @click.stop
            >
              <!-- Detail button inlined -->
              <button
                type="button"
                class="flex size-11 items-center justify-center rounded-lg text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-slate-6"
                :title="t('BROADCASTS.TABLE.ACTION_DETAIL')"
                :aria-label="t('BROADCASTS.TABLE.ACTION_DETAIL')"
                @click="handleViewDetail(broadcast)"
              >
                <span
                  class="i-lucide-arrow-up-right size-4.5"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>

          <div class="flex-1 flex flex-col justify-end mt-2 relative z-10">
            <!-- Progress Info -->
            <div class="mb-3">
              <div
                class="flex items-center justify-between text-xs font-semibold mb-1.5"
              >
                <span class="text-n-slate-11">
                  {{
                    t('BROADCASTS.TABLE.PROGRESS_HINT', {
                      sent: broadcast.sent_count || 0,
                      total: broadcast.total_count || 0,
                      failed: broadcast.failed_count || 0,
                    })
                  }}
                </span>
                <span class="text-n-slate-12 tabular-nums">{{
                  `${progressPercent(broadcast)}%`
                }}</span>
              </div>
              <ProgressBar
                :value="progressPercent(broadcast)"
                color="brand"
                size="sm"
                class="shadow-inner"
                :label="t('BROADCASTS.TABLE.PROGRESS')"
              />
            </div>

            <div
              class="flex items-center justify-between text-[11px] font-medium text-n-slate-10 pt-3 border-t border-n-weak/50"
            >
              <div class="flex items-center gap-1">
                <span class="i-lucide-calendar size-3.5" aria-hidden="true" />
                {{ formatDate(broadcast.created_at) }}
              </div>

              <!-- Bottom Actions -->
              <div
                class="flex items-center gap-0.5 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
                @click.stop
              >
                <button
                  v-if="broadcast.status === 'pending'"
                  type="button"
                  class="flex size-11 items-center justify-center rounded-lg text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-2 focus-visible:outline-n-brand"
                  :title="t('BROADCASTS.TABLE.ACTION_EDIT')"
                  :aria-label="t('BROADCASTS.TABLE.ACTION_EDIT')"
                  @click="handleEdit(broadcast)"
                >
                  <span class="i-lucide-pencil size-3.5" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  class="flex size-11 items-center justify-center rounded-lg text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-2 focus-visible:outline-n-brand disabled:cursor-not-allowed disabled:opacity-40"
                  :title="t('BROADCASTS.TABLE.ACTION_DUPLICATE')"
                  :aria-label="t('BROADCASTS.TABLE.ACTION_DUPLICATE')"
                  :disabled="isDuplicating === broadcast.id"
                  @click="handleDuplicate(broadcast)"
                >
                  <span
                    v-if="isDuplicating === broadcast.id"
                    class="i-lucide-loader-2 size-3.5 animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                  <span
                    v-else
                    class="i-lucide-copy size-3.5"
                    aria-hidden="true"
                  />
                </button>
                <button
                  v-if="broadcast.status === 'running'"
                  type="button"
                  class="flex size-11 items-center justify-center rounded-lg text-n-amber-11 transition-colors hover:bg-n-amber-3 focus-visible:outline-2 focus-visible:outline-n-brand disabled:opacity-50"
                  :title="t('BROADCASTS.TABLE.ACTION_PAUSE')"
                  :aria-label="t('BROADCASTS.TABLE.ACTION_PAUSE')"
                  :disabled="Boolean(pendingAction)"
                  @click="handlePause(broadcast)"
                >
                  <span class="i-lucide-pause size-3.5" aria-hidden="true" />
                </button>
                <button
                  v-if="broadcast.status === 'paused'"
                  type="button"
                  class="flex size-11 items-center justify-center rounded-lg text-n-teal-11 transition-colors hover:bg-n-teal-3 focus-visible:outline-2 focus-visible:outline-n-brand disabled:opacity-50"
                  :title="t('BROADCASTS.TABLE.ACTION_RESUME')"
                  :aria-label="t('BROADCASTS.TABLE.ACTION_RESUME')"
                  :disabled="Boolean(pendingAction)"
                  @click="handleResume(broadcast)"
                >
                  <span class="i-lucide-play size-3.5" aria-hidden="true" />
                </button>
                <button
                  v-if="
                    ['running', 'paused', 'pending'].includes(broadcast.status)
                  "
                  type="button"
                  class="flex size-11 items-center justify-center rounded-lg text-n-amber-10 transition-colors hover:bg-n-amber-2 hover:text-n-amber-11 focus-visible:outline-2 focus-visible:outline-n-brand disabled:opacity-50"
                  :title="t('BROADCASTS.TABLE.ACTION_CANCEL')"
                  :aria-label="t('BROADCASTS.TABLE.ACTION_CANCEL')"
                  :disabled="Boolean(pendingAction)"
                  @click="requestCancel(broadcast)"
                >
                  <span class="i-lucide-x size-3.5" aria-hidden="true" />
                </button>
                <button
                  v-if="
                    isAdmin &&
                    ['completed', 'cancelled', 'failed'].includes(
                      broadcast.status
                    )
                  "
                  type="button"
                  class="flex size-11 items-center justify-center rounded-lg text-n-ruby-10 transition-colors hover:bg-n-ruby-2 hover:text-n-ruby-11 focus-visible:outline-2 focus-visible:outline-n-brand disabled:opacity-50"
                  :title="t('BROADCASTS.TABLE.ACTION_DELETE')"
                  :aria-label="t('BROADCASTS.TABLE.ACTION_DELETE')"
                  :disabled="Boolean(pendingAction)"
                  @click="handleDelete(broadcast)"
                >
                  <span class="i-lucide-trash-2 size-3.5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>

    <Modal
      v-model:show="cancelModalOpen"
      size="small"
      :close-on-backdrop-click="pendingAction !== 'cancel'"
      :close-on-escape="pendingAction !== 'cancel'"
      :show-close-button="pendingAction !== 'cancel'"
      aria-labelledby="cancel-modal-title"
    >
      <div v-if="broadcastToCancel" class="p-6">
        <div class="flex items-start gap-4">
          <div
            class="flex size-12 flex-shrink-0 items-center justify-center rounded-2xl bg-n-amber-3 text-n-amber-11 ring-1 ring-n-amber-4 shadow-inner"
          >
            <span class="i-lucide-alert-triangle size-6" aria-hidden="true" />
          </div>
          <div class="flex-1 min-w-0">
            <h3
              id="cancel-modal-title"
              data-modal-title
              class="text-lg font-bold text-n-slate-12 tracking-tight leading-tight"
            >
              {{ t('BROADCASTS.CONFIRM_CANCEL_TITLE') }}
            </h3>
            <p class="text-sm font-semibold text-n-slate-11 mt-1 truncate">
              {{ broadcastToCancel.name }}
            </p>
            <p class="mt-3 text-sm font-medium text-n-slate-10 leading-relaxed">
              {{ t('BROADCASTS.CONFIRM_CANCEL_HINT') }}
            </p>
          </div>
        </div>
        <div
          class="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-n-weak/50"
        >
          <Button
            variant="ghost"
            color="slate"
            size="md"
            :disabled="pendingAction === 'cancel'"
            @click="closeCancel"
          >
            {{ t('BROADCASTS.CONFIRM_CANCEL_BACK') }}
          </Button>
          <Button
            variant="solid"
            color="amber"
            size="md"
            :is-loading="pendingAction === 'cancel'"
            :disabled="pendingAction === 'cancel'"
            @click="confirmCancel"
          >
            <span class="i-lucide-x-circle size-4.5" aria-hidden="true" />
            {{ t('BROADCASTS.CONFIRM_CANCEL_BUTTON') }}
          </Button>
        </div>
      </div>
    </Modal>

    <Modal
      v-model:show="deleteModalOpen"
      size="small"
      :close-on-backdrop-click="pendingAction !== 'delete'"
      :close-on-escape="pendingAction !== 'delete'"
      :show-close-button="pendingAction !== 'delete'"
      aria-labelledby="delete-modal-title"
    >
      <div v-if="broadcastToDelete" class="p-6">
        <div class="flex items-start gap-4">
          <div
            class="flex size-12 flex-shrink-0 items-center justify-center rounded-2xl bg-n-ruby-3 text-n-ruby-11 ring-1 ring-n-ruby-4 shadow-inner"
          >
            <span class="i-lucide-trash-2 size-6" aria-hidden="true" />
          </div>
          <div class="flex-1 min-w-0">
            <h3
              id="delete-modal-title"
              data-modal-title
              class="text-lg font-bold text-n-slate-12 tracking-tight leading-tight"
            >
              {{ t('BROADCASTS.CONFIRM_DELETE_TITLE') }}
            </h3>
            <p class="text-sm font-semibold text-n-slate-11 mt-1 truncate">
              {{ broadcastToDelete.name }}
            </p>
            <p class="mt-3 text-sm font-medium text-n-slate-10 leading-relaxed">
              {{ t('BROADCASTS.CONFIRM_DELETE_HINT') }}
            </p>
          </div>
        </div>
        <div
          class="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-n-weak/50"
        >
          <Button
            variant="ghost"
            color="slate"
            size="md"
            :disabled="pendingAction === 'delete'"
            @click="closeDelete"
          >
            {{ t('BROADCASTS.CONFIRM_DELETE_BACK') }}
          </Button>
          <Button
            variant="solid"
            color="ruby"
            size="md"
            :is-loading="pendingAction === 'delete'"
            :disabled="pendingAction === 'delete'"
            @click="confirmDelete"
          >
            <span class="i-lucide-trash-2 size-4.5" aria-hidden="true" />
            {{ t('BROADCASTS.CONFIRM_DELETE_BUTTON') }}
          </Button>
        </div>
      </div>
    </Modal>
  </div>
</template>
